import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { getMediaList } from 'services/api-service/center';
import { ControlCommand, ControlValue } from 'services/control-protocol';
import { IStoreState } from 'store/modules';
import StreamLive from './StreamLive';

interface Media {
  idx: string;
  type: 'V' | 'I';
  url: string;
  channelId: string;
}

interface Publisher {
  name: () => string;
}

interface CenterStreamProps {
  onControl: (cmd: ControlCommand, value: ControlValue, name: string) => void;
  refreshMediaTimestamp: number | null;
}

const CenterStream = ({ onControl, refreshMediaTimestamp }: CenterStreamProps) => {
  const curRoom: any = useSelector((store: IStoreState) => store.MainReducer.curRoom);
  const publishers = useSelector((store: IStoreState) => store.MainReducer.publishers);

  const [isExpire, setIsExpire] = useState(false);
  const [selected, setSelected] = useState<Media | null>(null);
  const [expand, setExpand] = useState(4); // 4 means not expanded
  const [medias, setMedias] = useState<Media[]>([]);
  const [streams, setStreams] = useState<Publisher[]>([]);

  const horizontalScrollRef = useRef<HTMLElement>(null);

  const onSelectItem = (item: Media) => {
    setSelected(item);
  };

  const onClose = () => {
    setSelected(null);
  };

  const onScrollClick = (direction: 'left' | 'right') => {
    if (!horizontalScrollRef.current) return;

    const scrollAmount = horizontalScrollRef.current.offsetWidth;
    const scrollPosition =
      direction === 'left'
        ? horizontalScrollRef.current.scrollLeft - scrollAmount
        : horizontalScrollRef.current.scrollLeft + scrollAmount;

    horizontalScrollRef.current.scrollTo({
      left: scrollPosition,
      behavior: 'smooth',
    });
  };

  const getMedia = async () => {
    if (!curRoom?.roomId) return;

    try {
      const response = await getMediaList(curRoom.roomId);
      setMedias(response.data);
    } catch (error) {
      console.error('Media list fetch error:', error);
    }
  };

  useEffect(() => {
    if (!refreshMediaTimestamp) return;

    const timer = setTimeout(() => {
      getMedia();
    }, 3000);

    return () => clearTimeout(timer);
  }, [refreshMediaTimestamp, curRoom]);

  useEffect(() => {
    if (!publishers?.length) {
      setStreams([]);
      return;
    }

    const filteredItems = publishers.filter(
      (item) => !item.name().includes('center__') && !item.name().startsWith('viewer__')
    );
    setStreams(filteredItems);
  }, [publishers]);

  useEffect(() => {
    if (curRoom) {
      setIsExpire(curRoom.isExpire);
      getMedia();
    } else {
      setIsExpire(false);
    }
    setSelected(null);
  }, [curRoom]);

  useEffect(() => {
    if (!medias.length || !horizontalScrollRef.current) return;

    horizontalScrollRef.current.scrollTo({
      left: horizontalScrollRef.current.scrollWidth,
      behavior: 'smooth',
    });
  }, [medias]);

  const handleMediaError = (event) => {
    const target = event.currentTarget;
    target.onerror = null; // 무한 루프 방지
    target.width = 50;
    target.height = 45;
    target.src = `${process.env.PUBLIC_URL}/images/img_novideo.svg`;
  };

  return (
    <CenterStreamTemplate>
      {!isExpire &&
        (expand === 4 ? (
          <HorizontalWrap>
            {streams.concat(Array(Math.max(0, 4 - streams.length)).fill(null)).map((content, index) => (
              <StreamLive
                key={index}
                index={index}
                participants={streams}
                expand={expand}
                onExpand={(idx) => setExpand(idx)}
                onUpateMedia={getMedia}
                onControl={onControl}
              />
            ))}
          </HorizontalWrap>
        ) : (
          <ExpandWrap>
            <StreamLive
              index={expand}
              participants={streams}
              expand={expand}
              onExpand={() => setExpand(4)}
              onUpateMedia={getMedia}
              onControl={onControl}
            />
          </ExpandWrap>
        ))}
      <PlayerWrap>
        {!selected ? (
          <img src={`${process.env.PUBLIC_URL}/images/img_novideo.svg`} alt="no video" />
        ) : (
          <Player>
            {selected.type === 'V' ? (
              <video src={selected.url} controls>
                <track kind="captions" srcLang="en" label="captions" />
              </video>
            ) : (
              <img
                src={selected.url}
                style={{ height: '100%', objectFit: 'contain' }}
                alt={`${selected.channelId}`}
                onError={handleMediaError}
              />
            )}
            <CloseButton onClick={onClose}>
              <img src={`${process.env.PUBLIC_URL}/images/img_chat_close.svg`} alt="close" />
            </CloseButton>
          </Player>
        )}
      </PlayerWrap>
      <HorizontalListWrap>
        <LeftButton onClick={() => onScrollClick('left')}>
          <img src={`${process.env.PUBLIC_URL}/images/img_left.svg`} alt="left" />
        </LeftButton>
        {curRoom && medias && (
          <ScrollWrap ref={horizontalScrollRef}>
            <ListWrap>
              {medias.map((item) => (
                <ItemWrap key={item.idx} onClick={() => onSelectItem(item)}>
                  {item.type === 'V' ? (
                    <ThumbnailWrap>
                      <video src={item.url}>
                        <track kind="captions" srcLang="en" label="captions" />
                      </video>
                      <PlayWrap
                        src={`${process.env.PUBLIC_URL}/images/btn_play.svg`}
                        width={30}
                        height={30}
                        alt="Play"
                      />
                    </ThumbnailWrap>
                  ) : (
                    <img
                      src={item.url}
                      style={{ height: '100%', objectFit: 'contain' }}
                      alt={`${item.idx}`}
                      onError={handleMediaError}
                    />
                  )}
                </ItemWrap>
              ))}
            </ListWrap>
          </ScrollWrap>
        )}
        <RightButton onClick={() => onScrollClick('right')}>
          <img src={`${process.env.PUBLIC_URL}/images/img_right.svg`} alt="right" />
        </RightButton>
      </HorizontalListWrap>
    </CenterStreamTemplate>
  );
};

const CenterStreamTemplate = styled.div`
  position: relative;
  width: 47.5rem;
  height: 43.44rem;
  background: #2d4254;
`;
const HorizontalWrap = styled.div`
  display: flex;
  width: 47.5rem;
  height: 35.625rem;
  background: #000;
  flex-flow: wrap;
  z-index: 2;
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const ExpandWrap = styled.div`
  width: 47.5rem;
  height: 35.625rem;
  background: #000;
  z-index: 2;
`;
const PlayerWrap = styled.div`
  position: absolute;
  top: 0;
  display: flex;
  width: 47.5rem;
  height: 35.625rem;
  align-items: center;
  justify-content: center;
`;
const Player = styled.div`
  width: 47.5rem;
  height: 35.625rem;
  padding: 0.5rem;
  background: #000;
  z-index: 2;
  text-align: center;
`;
const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: transparent;
  border: none;
  cursor: pointer;
  z-index: 1;
`;
const HorizontalListWrap = styled.div`
  position: absolute;
  bottom: 0;
  display: flex;
  width: 47.5rem;
  height: 7.9375rem;
  border: 1px solid #000;
  background: #2d4254;
`;
const LeftButton = styled.button`
  width: 1.875rem;
  height: 7.9375rem;
  background: #0071d8;
  cursor: pointer;
  border: 1px solid #000;
  appearance: none;
  padding: 0;
  outline: none;
  box-shadow: none;
  border-radius: 0;
  text-decoration: none;
  z-index: 2;
`;
const RightButton = styled(LeftButton)`
  position: absolute;
  right: 0;
  margin: auto 0 0 auto;
`;
const ScrollWrap = styled.nav`
  width: calc(100% - 4.5rem);
  height: inherit;
  margin: 0 5px;
  overflow-x: auto;
  overflow-y: hidden;
  white-space: nowrap;
  ::-webkit-scrollbar {
    display: none;
  }
`;
const ListWrap = styled.ul``;
const ItemWrap = styled.li`
  display: inline-block;
  width: 8.25rem;
  height: 6.1875rem;
  background: black 0px -2.276px / 101.01% 103.392% no-repeat;
  margin: 0.82rem 0.62rem;
  text-align: center;
  cursor: pointer;
  position: relative;
`;
const ThumbnailWrap = styled.div`
  display: inline-block;
  width: 8.25rem;
  height: 6.1875rem;
  text-align: center;
  cursor: pointer;
`;
const PlayWrap = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: transparent;
  z-index: 1;
`;

export default CenterStream;
